/**
 * Hook for interacting with the terminology Web Worker
 * Provides a typed API for terminology lookups
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import type { VeterinaryTerm, WorkerMessage, WorkerResponse } from '../worker';

// Singleton worker instance
let workerInstance: Worker | null = null;
let workerPromise: Promise<Worker> | null = null;
let isWorkerReady = false;
let terminologyVersion = '';
let workerListeners: Set<(ready: boolean, version: string) => void> = new Set();

/**
 * Initialize the worker (singleton pattern)
 */
function initializeWorker(): Promise<Worker> {
  if (workerInstance) {
    return Promise.resolve(workerInstance);
  }

  if (workerPromise) {
    return workerPromise;
  }

  workerPromise = new Promise((resolve, reject) => {
    try {
      // Dynamic import with proper webpack syntax
      const worker = new Worker(
        new URL('../worker/index.ts', import.meta.url),
        { type: 'module' }
      );

      worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
        const response = event.data;
        
        switch (response.type) {
          case 'ready':
            isWorkerReady = true;
            terminologyVersion = response.version;
            console.log('[useTerminology] Worker ready, version:', response.version);
            
            // Store version in localStorage for version checking
            localStorage.setItem('terminologyVersion', response.version);
            
            // Notify all listeners
            workerListeners.forEach(listener => listener(true, response.version));
            break;
            
          case 'error':
            console.error('[useTerminology] Worker error:', response.message);
            isWorkerReady = false;
            
            // Notify all listeners
            workerListeners.forEach(listener => listener(false, ''));
            break;
        }
      };

      worker.onerror = (error) => {
        console.error('[useTerminology] Worker error:', error);
        isWorkerReady = false;
        workerListeners.forEach(listener => listener(false, ''));
        reject(error);
      };

      // Send init message
      worker.postMessage({ type: 'init' } as WorkerMessage);
      
      workerInstance = worker;
      resolve(worker);
    } catch (error) {
      console.error('[useTerminology] Failed to create worker:', error);
      reject(error);
    }
  });

  return workerPromise;
}

/**
 * Lookup a single term
 */
export async function lookupTerm(word: string): Promise<VeterinaryTerm | undefined> {
  if (!isWorkerReady || !workerInstance) {
    // Return undefined if worker not ready
    return undefined;
  }

  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      resolve(undefined);
    }, 100); // 100ms timeout for individual lookups

    const handler = (event: MessageEvent<WorkerResponse>) => {
      const response = event.data;
      if (response.type === 'lookupResult' && response.word === word) {
        clearTimeout(timeout);
        workerInstance!.removeEventListener('message', handler);
        resolve(response.term);
      }
    };

    workerInstance!.addEventListener('message', handler);
    workerInstance!.postMessage({ type: 'lookup', word } as WorkerMessage);
  });
}

/**
 * Lookup multiple terms in batch
 */
export async function lookupBatch(words: string[]): Promise<Array<{ word: string; term?: VeterinaryTerm }>> {
  if (!isWorkerReady || !workerInstance) {
    // Return empty results if worker not ready
    return words.map(word => ({ word, term: undefined }));
  }

  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      resolve(words.map(word => ({ word, term: undefined })));
    }, 200); // 200ms timeout for batch lookups

    const handler = (event: MessageEvent<WorkerResponse>) => {
      const response = event.data;
      if (response.type === 'batchLookupResult') {
        clearTimeout(timeout);
        workerInstance!.removeEventListener('message', handler);
        resolve(response.results);
      }
    };

    workerInstance!.addEventListener('message', handler);
    workerInstance!.postMessage({ type: 'lookupBatch', words } as WorkerMessage);
  });
}

/**
 * Normalise an entire sentence (multi-word patterns + per-token lookups)
 */
export async function normaliseSentence(text: string): Promise<string> {
  if (!isWorkerReady || !workerInstance) return text;

  return new Promise(resolve => {
    const timeout = setTimeout(() => resolve(text), 300);

    const handler = (event: MessageEvent<WorkerResponse>) => {
      const res = event.data;
      if (res.type === 'normaliseResult') {
        clearTimeout(timeout);
        workerInstance!.removeEventListener('message', handler);
        resolve(res.text);
      }
    };

    workerInstance!.addEventListener('message', handler);
    workerInstance!.postMessage({ type: 'normalise', text } as WorkerMessage);
  });
}

/**
 * Reload terminology data silently
 */
export async function reloadSilently(): Promise<void> {
  if (!workerInstance) {
    await initializeWorker();
    return;
  }

  workerInstance.postMessage({ type: 'reload' } as WorkerMessage);
}

/**
 * Hook for using terminology in React components
 */
export function useTerminology() {
  const [ready, setReady] = useState(isWorkerReady);
  const [version, setVersion] = useState(terminologyVersion);
  const [error, setError] = useState<string | null>(null);
  const initRef = useRef(false);

  useEffect(() => {
    if (!initRef.current) {
      initRef.current = true;
      
      // Add listener for worker state changes
      const listener = (isReady: boolean, ver: string) => {
        setReady(isReady);
        setVersion(ver);
        if (!isReady && !ver) {
          setError('Failed to load terminology data');
        } else {
          setError(null);
        }
      };
      
      workerListeners.add(listener);
      // Immediately inform the new listener of current state
      listener(isWorkerReady, terminologyVersion);
      
      // Initialize worker
      initializeWorker().catch((err) => {
        console.error('[useTerminology] Failed to initialize worker:', err);
        setError(err.message || 'Failed to initialize terminology worker');
      });
      
      // Check for version updates
      const storedVersion = localStorage.getItem('terminologyVersion');
      if (storedVersion && storedVersion !== terminologyVersion && isWorkerReady) {
        console.log('[useTerminology] Version mismatch, reloading terminology');
        reloadSilently();
      }
      
      return () => {
        workerListeners.delete(listener);
      };
    }
  }, []);

  const lookup = useCallback(async (word: string): Promise<VeterinaryTerm | undefined> => {
    return lookupTerm(word);
  }, []);

  const batchLookup = useCallback(async (words: string[]): Promise<Array<{ word: string; term?: VeterinaryTerm }>> => {
    return lookupBatch(words);
  }, []);

  const reload = useCallback(async (): Promise<void> => {
    return reloadSilently();
  }, []);

  return {
    ready,
    version,
    error,
    lookup,
    batchLookup,
    reload,
  };
}

/**
 * Get all available terms for autocomplete/suggestions
 * Note: This is synchronous and returns empty array if worker not ready
 */
export function getAllTerms(): string[] {
  // This would require storing terms in main thread or additional worker API
  // For now, returning empty array as terms are only in worker
  return [];
}

/**
 * Check if terminology is ready for use
 */
export function isTerminologyReady(): boolean {
  return isWorkerReady;
}
